import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseModal } from '@/components/modal';
import type { ChatMessage } from '../types';
import { ChatAvatar } from './ChatAvatar';

interface MessageInfoModalProps {
  message: ChatMessage | null;
  isOpen: boolean;
  onClose: () => void;
  participants?: Array<{
    userId: string;
    user: { id: string; name: string; avatar?: string | null };
  }>;
}

export function MessageInfoModal({
  message,
  isOpen,
  onClose,
  participants,
}: MessageInfoModalProps) {
  const { t } = useTranslation();

  const formatTimestamp = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Determine which participants have "read" the message based on lastReadSeq
  const readByUsers = useMemo(() => {
    if (!message || !participants) return [];
    return participants.filter(
      (p) => p.userId !== message.senderId
    );
  }, [message, participants]);

  const getMessagePreview = () => {
    if (!message) return null;

    switch (message.type) {
      case 'image':
        return (
          <div className="flex items-center gap-2 text-neutral-800/70">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
                ry="2"
                strokeWidth="2"
              />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" strokeWidth="2" />
            </svg>
            <span className="font-medium">
              {t('messaging.messageInfo.imageMessage')}
            </span>
          </div>
        );
      case 'video':
        return (
          <div className="flex items-center gap-2 text-neutral-800/70">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <rect
                x="2"
                y="2"
                width="20"
                height="20"
                rx="2"
                ry="2"
                strokeWidth="2"
              />
              <polygon points="10,8 16,12 10,16" fill="currentColor" />
            </svg>
            <span className="font-medium">
              {t('messaging.messageInfo.videoMessage')}
            </span>
          </div>
        );
      case 'audio':
        return (
          <div className="flex items-center gap-2 text-neutral-800/70">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M9 18V5l12-2v13" strokeWidth="2" />
              <circle cx="6" cy="18" r="3" strokeWidth="2" />
              <circle cx="18" cy="16" r="3" strokeWidth="2" />
            </svg>
            <span className="font-medium">
              {t('messaging.messageInfo.audioMessage')}
            </span>
          </div>
        );
      case 'file':
        return (
          <div className="flex items-center gap-2 text-neutral-800/70">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"
                strokeWidth="2"
              />
              <polyline points="13 2 13 9 20 9" strokeWidth="2" />
            </svg>
            <span className="font-medium">
              {(message.mediaMetadata as Record<string, unknown>)?.fileName as string ||
                t('messaging.messageInfo.fileMessage')}
            </span>
          </div>
        );
      default:
        return (
          <p className="text-neutral-800 font-medium line-clamp-3">
            {message.content || t('messaging.messageInfo.emptyMessage')}
          </p>
        );
    }
  };

  return (
    <BaseModal
      isOpen={isOpen && !!message}
      onClose={onClose}
      mobileVariant="bottomsheet"
      maxWidth="max-w-md"
      ariaLabel={t('messaging.messageInfo.title')}
    >
      {message && (
        <>
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing">
            <div className="w-12 h-1 bg-slate-300 rounded-full" />
          </div>

          {/* Header */}
          <div className="px-5 py-3 border-b border-white/20 flex items-center justify-between">
            <h2 className="font-semibold text-xs sm:text-sm">
              {t('messaging.messageInfo.title')}
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              aria-label={t('common.close')}
            >
              <svg
                className="w-6 h-6 text-neutral-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M6 18L18 6M6 6l12 12"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-5 overflow-y-auto flex-1">
            {/* Message Preview */}
            <div className="bg-white/60 border border-white/20 p-4 rounded-xl">
              <div className="text-xs font-medium text-neutral-500 mb-2">
                {message.sender?.name || t('messaging.messageInfo.unknownSender')}
              </div>
              {getMessagePreview()}
            </div>

            {/* Sent Timestamp */}
            <div className="bg-white/60 border border-white/20 p-4 rounded-xl">
              <div className="flex items-center gap-3">
                {/* Sent icon */}
                <div className="w-8 h-8 bg-slate-700 border border-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M5 12l5 5L20 7"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-neutral-800">
                    {t('messaging.messageInfo.sent')}
                  </p>
                  <p className="text-xs font-medium text-neutral-500">
                    {formatTimestamp(message.createdAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Delivered Status */}
            <div className="bg-white/60 border border-white/20 p-4 rounded-xl">
              <div className="flex items-center gap-3">
                {/* Delivered icon (double check) */}
                <div className="w-8 h-8 bg-slate-700 border border-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M1 12l5 5L16 7"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 12l5 5L23 7"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-neutral-800">
                    {t('messaging.messageInfo.delivered')}
                  </p>
                  <p className="text-xs font-medium text-neutral-500">
                    {formatTimestamp(message.createdAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Read By Section */}
            <div className="bg-white/60 border border-white/20 p-4 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                {/* Read icon (eye) */}
                <div className="w-8 h-8 bg-slate-700 border border-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                      strokeWidth="2"
                    />
                    <circle cx="12" cy="12" r="3" strokeWidth="2" />
                  </svg>
                </div>
                <p className="font-medium text-neutral-800">
                  {t('messaging.messageInfo.readBy')}
                </p>
              </div>

              {readByUsers.length > 0 ? (
                <div className="space-y-3">
                  {readByUsers.map((participant) => (
                    <div
                      key={participant.userId}
                      className="flex items-center gap-3 p-2 border border-white/20/20 bg-white/20 rounded-xl"
                    >
                      {/* Avatar */}
                      <ChatAvatar
                        src={participant.user.avatar}
                        name={participant.user.name}
                        fallbackClass="bg-slate-700 text-white"
                        className="flex-shrink-0"
                      />

                      {/* User info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-neutral-800 truncate">
                          {participant.user.name}
                        </p>
                        <p className="text-xs font-medium text-neutral-500">
                          {formatTimestamp(message.createdAt)}
                        </p>
                      </div>

                      {/* Read checkmark */}
                      <div className="flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-slate-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M1 12l5 5L16 7"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8 12l5 5L23 7"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-medium text-neutral-500 text-center py-3">
                  {t('messaging.messageInfo.noReaders')}
                </p>
              )}
            </div>

            {/* Edited info (if applicable) */}
            {message.isEdited && message.editedAt && (
              <div className="bg-white/60 border border-white/20 p-4 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-100 border border-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-neutral-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                        strokeWidth="2"
                      />
                      <path
                        d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-800">
                      {t('messaging.messageInfo.edited')}
                    </p>
                    <p className="text-xs font-medium text-neutral-500">
                      {formatTimestamp(message.editedAt)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom safe area spacing */}
          <div className="h-6" />
        </>
      )}
    </BaseModal>
  );
}
